import React from 'react';
import { ITableColumns, ITableDataSource } from 'models/table';
import Button from 'components/atoms/Button';
import { IAccessoriesAttachments, IAccessoriesTransactions } from 'models/ancillaries';
import { EIconTypes } from 'constants/Icons';
import { EButtonVariants } from 'constants/Buttons';
import { downloadAncillaryAttachment } from 'requests/attachment';
import { Link } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { EFileType } from 'constants/FileType';
import { parseDictionary } from 'helpers/dictionary';
import { formatDate } from 'utils/date';
import { IDictionary } from 'models/dictionary';

interface IAncillariesTranslationsTableColumns {
  handleDelete: ((arg: string) => void) | null;
  discardableTransactionId: string | null;
  dateFormat: string;
}

export const AccessoriesTranslationsTableColumns = ({
  handleDelete,
  discardableTransactionId,
  dateFormat,
}: IAncillariesTranslationsTableColumns): ITableColumns[] => [
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
      key: 'transactionType',
      title: 'accessoryDetails.transactions.transactionType.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
      key: 'date',
      title: 'accessoryDetails.transactions.DateAndTime.column',
      dateFormat,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f511',
      key: 'createdDate',
      title: 'accessoryDetails.transactions.createDate.column',
      dateFormat,
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
      key: 'owner',
      title: 'accessoryDetails.transactions.owner.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f514',
      key: 'keeper',
      title: 'accessoryDetails.transactions.keeper.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
      key: '',
      title: 'accessoriesTable.accordion.action.column',
      isSortable: false,
      width: '80px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <div>
          {props.detailsVisible && (
            <Link to={ERouteLinks.ViewTransaction.replace(':id', props.id)}>
              <Button variant={EButtonVariants.outlined} tableButton icon={EIconTypes.view} />
            </Link>
          )}
          {props.id === discardableTransactionId && handleDelete && (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props.id)}
            />
          )}
        </div>
      ),
    },
  ];

export const mapAccessoriesTranslationsToDataSource = (
  transactions: IAccessoriesTransactions[],
): ITableDataSource[] =>
  transactions.map((transaction) => ({
    id: transaction.uniqueId,
    date: transaction.date,
    createdDate: transaction.createdDate,
    transactionType: transaction.transactionType,
    owner: transaction.owner,
    keeper: transaction.keeper,
    detailsVisible: transaction.detailsVisible,
  }));

interface IAncillariesAttachmentsTableColumns {
  ancillaryId: string;
  handleView: (arg: string) => void;
  handleDelete: (arg: string) => void;
  dateFormat: string;
  hideManagement: boolean;
}

export const AccessoriesAttachmentsTableColumns = ({
  ancillaryId,
  handleView,
  handleDelete,
  dateFormat,
  hideManagement,
}: IAncillariesAttachmentsTableColumns): ITableColumns[] => [
    {
      id: '36fa4777-5f90-434a-adf1-2b3aea11d685',
      key: 'createdDate',
      title: 'accessoryDetails.attachments.dateAndTime.column',
      dateFormat,
    },
    {
      id: 'c3c4e92a-8276-4695-9b6d-9c3566e9501a',
      key: 'name',
      title: 'accessoryDetails.attachments.attachmentName.column',
    },
    {
      id: '063918e4-7492-40b2-9445-4d8d43e24f07',
      key: 'size',
      title: 'accessoryDetails.attachments.attachmentSize.column',
    },
    {
      id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
      key: 'transactionType',
      title: 'accessoryDetails.attachments.transactionType.column',
      isSortable: false,
    },
    {
      id: '9c4ca471-15cd-4e22-90ef-7b57b7622955',
      key: '',
      title: 'accessoriesTable.accordion.action.column',
      isSortable: false,
      width: '80px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <div>
          <Button
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.download}
            onClick={() =>
              downloadAncillaryAttachment(ancillaryId, props.id, props.name, props.type)
            }
          />

          {props.isReport && props.type.toLowerCase() !== EFileType.XLSX && (
            <Button
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.view}
              onClick={() => handleView && handleView(props.id)}
            />
          )}
          {!hideManagement &&
            (!props.isReport || props.type.toLowerCase() === EFileType.XLSX) && (
              <Button
                variant={EButtonVariants.outlined}
                tableButton
                icon={EIconTypes.delete}
                onClick={() => handleDelete && handleDelete(props.id)}
              />
            )}
        </div>
      ),
    },
  ];

export const mapAccessoriesAttachmentsToDataSource = (
  attachments: IAccessoriesAttachments[],
  transactionType: IDictionary[] | null,
  dateFormat: string,
): ITableDataSource[] =>
  attachments.map((attachment) => ({
    id: attachment.uniqueId,
    createdDate: attachment.createdDate,
    name: attachment.name,
    type: attachment.type,
    size: Math.trunc(Number(attachment.size) / 1024),
    transactionType: `${attachment.transactionTypeUniqueId
      ? parseDictionary(transactionType, 'name')[attachment.transactionTypeUniqueId]
      : ''
      } ${formatDate(attachment.transactionTime, dateFormat)}`,
  }));
